/**
 * AmbulanceMapView — Leaflet map showing the customer's location + all
 * nearby ambulance partners as markers, with real-road distance labels.
 * Tapping a partner marker fires `onSelect(partner)` (jump into booking).
 *
 * Props:
 *   partners: [{ id, name, lat, lng, vehicle_classes, per_km_rate, base_fare }]
 *   onSelect(partner): callback when a partner card / marker is clicked
 */
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { Link } from "react-router-dom";
import { Ambulance, NavigationArrow, MapPin } from "@phosphor-icons/react";
import { custApi } from "../lib/customerApi";

// Ensure default marker sprite works (Webpack path gotcha)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const userIcon = new L.DivIcon({
  className: "",
  html: `<div style="width:22px;height:22px;background:#2563EB;border:3px solid white;
                    border-radius:50%;box-shadow:0 3px 10px rgba(37,99,235,0.55);
                    animation:amb-pulse 1.6s ease-out infinite;"></div>
         <style>@keyframes amb-pulse{0%{box-shadow:0 0 0 0 rgba(37,99,235,.45)}
                                        100%{box-shadow:0 0 0 18px rgba(37,99,235,0)}}</style>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

const ambulanceIcon = (label) => new L.DivIcon({
  className: "",
  html: `<div style="width:34px;height:34px;background:#DC2626;border:2px solid white;color:white;
                    border-radius:8px;display:flex;flex-direction:column;align-items:center;
                    justify-content:center;font-weight:800;font-size:11px;
                    box-shadow:0 4px 14px rgba(220,38,38,0.45);letter-spacing:0.5px;">
           ${label || "AMB"}
         </div>`,
  iconSize: [34, 34], iconAnchor: [17, 17],
});

function FitBounds({ pts }) {
  const map = useMap();
  useEffect(() => {
    if (!pts?.length) return;
    const bounds = L.latLngBounds(pts.map(p => [p[0], p[1]]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [pts, map]);
  return null;
}

const FALLBACK = { lat: 8.5241, lng: 76.9366 }; // Trivandrum

export default function AmbulanceMapView({ partners = [], onSelect }) {
  const [userPos, setUserPos] = useState(null);
  const [geoErr, setGeoErr] = useState("");
  const [distances, setDistances] = useState({}); // partner_id → { km, min }
  const [q, setQ] = useState("");

  // Try to grab user's current location.
  useEffect(() => {
    if (!navigator.geolocation) { setGeoErr("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => setGeoErr(err.message || "Location denied"),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  // Fetch real-road distance from user → each partner (in parallel).
  useEffect(() => {
    if (!userPos) return;
    const eligible = partners.filter(p => p.lat != null && p.lng != null);
    let cancelled = false;
    (async () => {
      const results = await Promise.allSettled(
        eligible.map(p => custApi.post("/me/partners/route", {
          from_lat: userPos.lat, from_lng: userPos.lng,
          to_lat: p.lat, to_lng: p.lng,
        })),
      );
      if (cancelled) return;
      const out = {};
      eligible.forEach((p, i) => {
        const r = results[i];
        if (r.status === "fulfilled") {
          out[p.id] = { km: r.value.data.distance_km, min: r.value.data.duration_min };
        }
      });
      setDistances(out);
    })();
    return () => { cancelled = true; };
  }, [userPos, partners]);

  const eligible = useMemo(
    () => partners.filter(p => p.lat != null && p.lng != null),
    [partners],
  );

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return eligible;
    return eligible.filter(p =>
      (p.name || "").toLowerCase().includes(s)
      || (p.address || "").toLowerCase().includes(s),
    );
  }, [eligible, q]);

  // Sort by known distance ascending; unknowns to the bottom.
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const da = distances[a.id]?.km ?? 1e9;
      const dbb = distances[b.id]?.km ?? 1e9;
      return da - dbb;
    });
  }, [filtered, distances]);

  const center = userPos || (eligible[0]
    ? { lat: eligible[0].lat, lng: eligible[0].lng }
    : FALLBACK);

  const fitPoints = [
    ...(userPos ? [[userPos.lat, userPos.lng]] : []),
    ...eligible.map(p => [p.lat, p.lng]),
  ];

  return (
    <div className="space-y-3" data-testid="amb-map-view">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <MapPin size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8A8780]"/>
          <input
            data-testid="amb-map-search"
            placeholder="Search ambulance provider or area…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full h-9 pl-8 pr-3 rounded-lg border border-[#E5E2D9] text-sm bg-white"
          />
        </div>
        <button
          type="button"
          data-testid="amb-map-locate"
          onClick={() => {
            if (!navigator.geolocation) return;
            navigator.geolocation.getCurrentPosition(
              (pos) => setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
              (err) => setGeoErr(err.message || "Location denied"),
              { enableHighAccuracy: true, timeout: 8000 },
            );
          }}
          className="h-9 px-3 rounded-lg bg-[#0B2B66] text-white text-xs font-semibold inline-flex items-center gap-1 hover:bg-[#08214E]"
        >
          <NavigationArrow size={12} weight="fill"/> My location
        </button>
      </div>

      {geoErr && (
        <div data-testid="amb-map-geo-error"
              className="text-[11px] text-[#7A2E18] bg-[#FCEEEA] border border-[#F0DAD3] rounded-md p-2">
          Location unavailable ({geoErr}). Showing partners across the region — grant location access for accurate distances.
        </div>
      )}

      <div className="rounded-xl overflow-hidden border border-[#E5E2D9] shadow-sm"
            style={{ height: 380 }} data-testid="amb-leaflet-container">
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={13}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {userPos && (
            <Marker position={[userPos.lat, userPos.lng]} icon={userIcon}>
              <Popup>You are here</Popup>
            </Marker>
          )}
          {sorted.map((p, i) => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={ambulanceIcon((p.vehicle_classes || [])[0] || "AMB")}
              eventHandlers={{ click: () => onSelect && onSelect(p) }}
            >
              <Popup>
                <div className="min-w-[180px]">
                  <div className="font-semibold text-[13px] text-[#1A1A1A]">{p.name}</div>
                  {p.address && <div className="text-[11px] text-[#5C5A55]">{p.address}</div>}
                  <div className="text-[11px] text-[#7A2E18] mt-1">
                    {distances[p.id]
                      ? <>~{distances[p.id].km} km · {Math.round(distances[p.id].min || 0)} min</>
                      : <>Distance loading…</>}
                  </div>
                  <div className="text-[10px] mt-1 text-[#5C5A55]">
                    {(p.vehicle_classes || []).join(" · ")} {p.per_km_rate ? `· ₹${p.per_km_rate}/km` : ""}
                  </div>
                  <Link
                    to={`/me/health/ambulance/${p.id}`}
                    data-testid={`amb-map-book-${p.id}`}
                    className="mt-2 inline-block text-[11px] font-bold text-white bg-[#DC2626] px-2.5 py-1 rounded"
                  >Book now →</Link>
                </div>
              </Popup>
            </Marker>
          ))}
          <FitBounds pts={fitPoints}/>
        </MapContainer>
      </div>

      {/* Sorted-by-distance list under the map */}
      <div className="space-y-2" data-testid="amb-map-list">
        {sorted.slice(0, 12).map(p => (
          <Link
            key={p.id}
            to={`/me/health/ambulance/${p.id}`}
            data-testid={`amb-map-row-${p.id}`}
            className="flex items-center gap-3 p-3 bg-white rounded-lg border border-[#E5E2D9] hover:border-[#7A2E18] hover:shadow-sm transition"
          >
            <div className="w-10 h-10 rounded-full bg-[#FCEEEA] grid place-items-center text-[#7A2E18] shrink-0">
              <Ambulance size={18} weight="duotone"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{p.name}</div>
              <div className="text-[11px] text-[#5C5A55] truncate">{p.address || "—"}</div>
              <div className="text-[10px] text-[#8A8780] mt-0.5">
                {(p.vehicle_classes || []).join(" · ")}
                {p.per_km_rate ? ` · ₹${p.per_km_rate}/km` : ""}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-sm font-bold text-[#DC2626] font-mono">
                {distances[p.id]
                  ? <>{distances[p.id].km} km</>
                  : <span className="text-[#8A8780] text-xs">—</span>}
              </div>
              {distances[p.id]?.min != null && (
                <div className="text-[10px] text-[#8A8780]">~{Math.round(distances[p.id].min)} min</div>
              )}
            </div>
          </Link>
        ))}
        {!sorted.length && (
          <div className="p-8 text-center text-sm text-[#8A8780] bg-white border border-dashed border-[#E5E2D9] rounded-lg">
            No ambulance partners with location data yet.
          </div>
        )}
      </div>
    </div>
  );
}
